import { createApi } from '@reduxjs/toolkit/query/react';

import {
  BOCustomerDTO,
  Customer,
  CustomerUpdateViewRequest,
  NotificationsDTO,
  PetVerticalListResponse,
  removeDeviceResponse,
  ThirdPartyLoginsResponse,
  TrustedDevicesListResponse,
  UpdateCustomerContactInfo,
  UpdateCustomerWebsiteInfoDTO,
  ViewProfileDetails,
} from '@/types/Customer';
import { ResponseError } from '@/types/Error';
import { CorporateAccount } from '@/types/Signup';
import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

const contentTypes = {
  applicationJson: 'application/json',
};

export const accountApi = createApi({
  reducerPath: 'accountApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: `Account` }),
  tagTypes: ['PawPayAccountType', 'Customers', 'NotificationDetails', 'CorporateAccounts', 'TrustedDeviceList'],
  endpoints: builder => ({
    getPawPayAccountType: builder.query<SuccessResponse<number>, void>({
      query: () => ({
        url: '/GetPawPayAccountType',
        next: { tags: ['PawPayAccountType'] },
      }),
      transformResponse: (response: SuccessResponse<number>, meta, arg) => response,
      providesTags: ['PawPayAccountType'],
    }),
    getCustomerInformation: builder.query<SuccessResponse<Customer | BOCustomerDTO>, void>({
      query: () => ({
        url: '/GetCustomerInformation',
        next: { tags: ['Customers'] },
      }),
      transformResponse: (response: SuccessResponse<Customer>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['Customers'],
    }),
    updateCustomerContactInfo: builder.mutation<SuccessResponse<unknown>, UpdateCustomerContactInfo>({
      query: payload => ({
        url: '/UpdateCustomerContactInfo',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['Customers'], // Invalidate the 'Customers' tag to refetch the customer info after update
    }),
    updateCustomerBasicInfo: builder.mutation<SuccessResponse<unknown>, CustomerUpdateViewRequest>({
      query: payload => ({
        url: '/UpdatePerosnalInfo',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['Customers'], // Invalidate the 'Customers' tag to refetch the customer info after update
    }),
    updateCustomerWebsiteInfo: builder.mutation<SuccessResponse<unknown>, UpdateCustomerWebsiteInfoDTO>({
      query: payload => ({
        url: '/UpdateCustomerWebsiteInfo',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['Customers'], // Invalidate the 'Customers' tag to refetch the customer info after update
    }),
    getNotificationDetails: builder.query<SuccessResponse<NotificationsDTO>, void>({
      query: () => ({
        url: '/GetNotificationDetails',
        method: 'GET',
      }),
      transformResponse: (response: SuccessResponse<NotificationsDTO>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['NotificationDetails'],
    }),
    subscribeEmailNotifications: builder.mutation<SuccessResponse<unknown>, string>({
      query: email => ({
        url: `/SubscribeEmailNotifications?email=${encodeURIComponent(email)}`, // Send email as a query parameter
        method: 'POST',
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => {
        return {
          status: response.status,
          data: response.data,
        } as ResponseError;
      },
    }),
    unsubscribeEmailNotifications: builder.mutation<SuccessResponse<unknown>, void>({
      query: () => ({
        url: '/UnsubscribeEmailNotifications',
        method: 'POST',
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    unsubscribePetProSMS: builder.mutation<SuccessResponse<unknown>, void>({
      query: () => ({
        url: '/UnsubscribePetProSMS',
        method: 'POST',
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    subscribePetProSMS: builder.mutation<SuccessResponse<unknown>, void>({
      query: () => ({
        url: '/SubscribePetProSMS',
        method: 'POST',
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getViewProfileDetails: builder.query<SuccessResponse<ViewProfileDetails>, void>({
      query: () => ({
        url: '/viewprofile',
        method: 'GET',
      }),
      transformResponse: (response: SuccessResponse<ViewProfileDetails>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getCorporateAccountsDetails: builder.query<SuccessResponse<CorporateAccount[]>, void>({
      query: () => ({
        url: '/GetCorporateAccountsDetails',
        method: 'GET',
      }),
      transformResponse: (response: SuccessResponse<CorporateAccount[]>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      providesTags: ['CorporateAccounts'],
    }),
    sendCodeAccountSettings: builder.mutation<
      SuccessResponse<{ smsSendTo: string; emailSendTo: string; expiry: string }>,
      { phonenumber?: string; email?: string }
    >({
      query: payload => ({
        url: '/SendCodeAccountSettings',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (
        response: SuccessResponse<{ smsSendTo: string; emailSendTo: string; expiry: string }>,
        meta,
        arg
      ) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    resendCodeAccountSettings: builder.mutation<
      SuccessResponse<{ smsSendTo: string; emailSendTo: string; expiry: string }>,
      { phonenumber?: string; email?: string }
    >({
      query: payload => ({
        url: '/ResendCodeAccountSettings',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (
        response: SuccessResponse<{ smsSendTo: string; emailSendTo: string; expiry: string }>,
        meta,
        arg
      ) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    toggle2FA: builder.mutation<SuccessResponse<{ status: number }>, { code: string }>({
      query: payload => ({
        url: '/toggle2fa',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: SuccessResponse<{ status: number }>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['Customers', 'TrustedDeviceList'], // Invalidate to refetch customer data
    }),
    getThirdPartyLogins: builder.query<SuccessResponse<ThirdPartyLoginsResponse>, void>({
      query: () => ({ url: `/ThirdPartyLogins`, method: 'GET' }),
      transformResponse: (response: SuccessResponse<ThirdPartyLoginsResponse>, meta, arg) => response,
    }),
    getTrustedDevicesList: builder.query<SuccessResponse<TrustedDevicesListResponse>, void>({
      query: () => ({ url: `/DevicesList`, method: 'GET', next: { tags: ['TrustedDeviceList'] } }),
      transformResponse: (response: SuccessResponse<TrustedDevicesListResponse>, meta, arg) => response,
      providesTags: ['TrustedDeviceList'],
    }),
    logoutAllDevices: builder.mutation<SuccessResponse<unknown>, void>({
      query: () => ({ url: `/LogoutAllAccountSettings`, method: 'GET' }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
    }),

    removeTrustedDevice: builder.mutation<SuccessResponse<removeDeviceResponse>, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: `/removedeviceaccountsettings`,
        method: 'POST',
        body: JSON.stringify({ sessionId }),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<removeDeviceResponse>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['TrustedDeviceList'] : []),
    }),
    revokedDeviceSession: builder.mutation<SuccessResponse<removeDeviceResponse>, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: `/revokesessionaccountsettings`,
        method: 'POST',
        body: JSON.stringify({ sessionId }),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<removeDeviceResponse>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: (result, error, arg) => (!error && result?.success ? ['TrustedDeviceList'] : []),
    }),
    manageBlogs: builder.mutation<SuccessResponse<unknown>, void>({
      query: () => ({
        url: '/ManageBlogs',
        method: 'POST',
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    privacyPreference: builder.mutation<SuccessResponse<unknown>, { cookiePreference: boolean }>({
      query: ({ cookiePreference }) => ({
        url: '/SaveCookiePreference',
        method: 'POST',
        body: { cookiePreference },
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['Customers'],
    }),
    updateCustomerPetVertical: builder.mutation<SuccessResponse<unknown>, { petVertical: string }>({
      query: payload => ({
        url: '/SavePetVertical',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      invalidatesTags: ['Customers'],
    }),
    getPetVerticalList: builder.query<SuccessResponse<PetVerticalListResponse>, void>({
      query: () => ({
        url: '/petVerticals',
        method: 'GET',
      }),
      transformResponse: (response: SuccessResponse<PetVerticalListResponse>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
  }),
});

export const {
  useGetPawPayAccountTypeQuery,
  useGetCustomerInformationQuery,
  useUpdateCustomerContactInfoMutation,
  useUpdateCustomerBasicInfoMutation,
  useUpdateCustomerWebsiteInfoMutation,
  useGetNotificationDetailsQuery,
  useSubscribeEmailNotificationsMutation,
  useUnsubscribeEmailNotificationsMutation,
  useUnsubscribePetProSMSMutation,
  useSubscribePetProSMSMutation,
  useGetViewProfileDetailsQuery,
  useGetCorporateAccountsDetailsQuery,
  useSendCodeAccountSettingsMutation,
  useResendCodeAccountSettingsMutation,
  useToggle2FAMutation,
  useGetThirdPartyLoginsQuery,
  useGetTrustedDevicesListQuery,
  useLogoutAllDevicesMutation,
  useRemoveTrustedDeviceMutation,
  useRevokedDeviceSessionMutation,
  useManageBlogsMutation,
  usePrivacyPreferenceMutation,
  useUpdateCustomerPetVerticalMutation,
  useGetPetVerticalListQuery,
} = accountApi;
